import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { Box, IconButton, List, ListItemButton, Paper, Tooltip, Typography, useMediaQuery } from '../../../ui/core';
import { ChevronLeftIcon, ChevronRightIcon } from '../../../ui/icons';
import { surfaceCardSx, railItemSx, scrollY, useTheme } from '../../../ui/styles';
import { TripDisplayStatusOrder } from '../../../models/Trips';
import { filterDailyTrips } from '../../../helpers/utils/trip';
import { useQuery } from '../../../hooks';
import { TripDisplayStatusIcon } from './TripDisplayStatus';

// Secondary rail for the daily Trips view — the same [rail | content] pattern as
// Promotions and Driver Triage. Lists the core trip statuses with a live count,
// and clicking one scrolls the grouped grid to that status's section.
//
// Desktop only. Below `md` there is no room for a 236px column beside the content,
// and what it does — jump to a section of a tall grid — isn't what a phone needs:
// the toolbar's Filters menu narrows the list to one status instead. See
// TripsFilters.
//
// The rail and the grid are separate cards, so we reach the grid through the
// DOM. `root` scopes those queries to the Trips shell subtree (passed via
// scopeRef) rather than a global document query, so it targets THIS route's grid
// even if another grid is ever on the page. The group header rows carry
// `data-rowid="DataGrid_group_<status>"`.
// The rail collapses to a narrow column of icon + count, giving the grid its
// width back. The choice is per-browser, so it sticks across reloads.
const COLLAPSED_STORAGE_KEY = 'admin-trips-status-rail-collapsed';
const EXPANDED_W = 236;
const COLLAPSED_W = 64;

const readCollapsed = () => {
  try {
    return window.localStorage.getItem(COLLAPSED_STORAGE_KEY) === 'true';
  } catch {
    return false;
  }
};

const scrollToStatus = (root, status) => {
  const container = root.querySelector('.DataGrid-main');
  if (!container) return;
  const content = container.querySelector('.DataGrid-content');
  const row = container.querySelector(`.DataGrid-row[data-rowid="DataGrid_group_${status}"]`);
  if (!content || !row) return;
  const header = container.querySelector('.DataGrid-headers');
  const headerHeight = header ? header.offsetHeight : 0;
  // Sum the heights of the rows before the target to get its natural position.
  // We can't use the target's own offsetTop or getBoundingClientRect: the group
  // headers are sticky, so both report the *pinned* position (≈headerHeight)
  // when the target is currently stuck at the top — which made clicking a higher
  // status barely scroll. offsetHeight is unaffected by sticky, so this stays
  // correct from any scroll position. The reads happen in a single pass, so this
  // forces at most one synchronous layout flush per click (an infrequent user
  // action, not a per-frame path), which is an acceptable cost for correctness.
  let naturalTop = 0;
  let sibling = row.previousElementSibling;
  while (sibling) {
    naturalTop += sibling.offsetHeight;
    sibling = sibling.previousElementSibling;
  }
  const target = content.offsetTop + naturalTop - headerHeight;
  container.scrollTo({ top: Math.max(0, target), behavior: 'smooth' });
};

const TripsStatusRail = ({ scopeRef }) => {
  const theme = useTheme();
  const compact = useMediaQuery(theme.breakpoints.down('md'));
  const [active, setActive] = useState(null);
  const [collapsed, setCollapsed] = useState(readCollapsed);
  const data = useSelector((state) => state.trips.dailyTrips.data);

  // Scrollspy: as the grid scrolls, highlight whichever status section is
  // currently under the sticky column headers.
  useEffect(() => {
    const root = (scopeRef && scopeRef.current) || document;
    const container = root.querySelector('.DataGrid-main');
    if (!container) return undefined;
    const headerEl = container.querySelector('.DataGrid-headers');
    // Column-header height is static for a scroll session — read it once here
    // rather than on every animation frame in compute().
    const headerHeight = headerEl ? headerEl.offsetHeight : 0;
    let raf = null;

    const compute = () => {
      raf = null;
      const line = container.getBoundingClientRect().top + headerHeight;
      const groups = container.querySelectorAll('.DataGrid-row[data-rowid^="DataGrid_group_"]');
      let current = null;
      groups.forEach((g) => {
        // The last group whose header has reached (or passed) the sticky line is
        // the section currently in view.
        if (g.getBoundingClientRect().top <= line + 4) {
          current = g.getAttribute('data-rowid').replace('DataGrid_group_', '');
        }
      });
      // Before the first section reaches the line, treat the first as in view.
      if (!current && groups.length) {
        current = groups[0].getAttribute('data-rowid').replace('DataGrid_group_', '');
      }
      setActive((prev) => (prev === current ? prev : current));
    };

    const schedule = () => {
      if (raf == null) raf = requestAnimationFrame(compute);
    };

    container.addEventListener('scroll', schedule, { passive: true });

    // The grouped grid renders its group rows a tick after data arrives, and when
    // the content fits without scrolling no scroll event fires — so watch the rows
    // for changes and recompute then too. Group rows are direct children of
    // .DataGrid-content, so childList without subtree is enough (and avoids firing
    // on every descendant text change, e.g. live cell updates).
    const content = container.querySelector('.DataGrid-content');
    const observer = new MutationObserver(schedule);
    if (content) observer.observe(content, { childList: true, subtree: false });

    schedule();

    return () => {
      container.removeEventListener('scroll', schedule);
      observer.disconnect();
      if (raf != null) cancelAnimationFrame(raf);
    };
    // `compact` is a dep because the grid this reaches for only exists above `md`;
    // crossing the breakpoint has to redo the lookup.
  }, [data, scopeRef, compact]);

  // Raw query params are stable primitives; parse the cities list inside the memo
  // so the dep list can reference them directly (no array-identity churn).
  const { citiesIds: citiesIdsParam, ghostTripsOnly: ghostParam, pendingReassignmentOnly: pendingParam } = useQuery();
  const ghostTripsOnly = ghostParam === 'true';
  const pendingReassignmentOnly = pendingParam === 'true';

  // Count trips per status against the SAME filtered set the table shows, so the
  // rail counts always match the group headers.
  const counts = useMemo(() => {
    const citiesIds = (citiesIdsParam || '').split(',').filter(Boolean);
    const filtered = filterDailyTrips(data || [], { citiesIds, ghostTripsOnly, pendingReassignmentOnly });
    const map = {};
    TripDisplayStatusOrder.forEach((s) => {
      map[s] = 0;
    });
    filtered.forEach((t) => {
      if (map[t.displayStatus] != null) map[t.displayStatus] += 1;
    });
    return map;
  }, [data, citiesIdsParam, ghostTripsOnly, pendingReassignmentOnly]);

  // After the hooks, never before — a conditional hook call would break the order.
  if (compact) return null;

  const toggleCollapsed = () => {
    const next = !collapsed;
    setCollapsed(next);
    try {
      window.localStorage.setItem(COLLAPSED_STORAGE_KEY, String(next));
    } catch {
      // localStorage unavailable (private mode / blocked) — keep in-memory only.
    }
  };

  const ToggleIcon = collapsed ? ChevronRightIcon : ChevronLeftIcon;

  return (
    <Paper
      elevation={0}
      sx={{
        ...surfaceCardSx,
        width: collapsed ? COLLAPSED_W : EXPANDED_W,
        flexShrink: 0,
        transition: 'width 0.15s ease',
      }}
    >
      <Box
        sx={{
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          ...(collapsed
            ? { p: '12px 8px', justifyContent: 'center' }
            : { p: '16px', pb: '12px', pr: '10px', justifyContent: 'space-between' }),
        }}
      >
        {!collapsed && <Typography sx={{ fontSize: '15px', fontWeight: 600 }}>Status</Typography>}
        <Tooltip title={collapsed ? 'Expand status' : 'Collapse status'} placement="right">
          <IconButton
            size="small"
            onClick={toggleCollapsed}
            aria-label={collapsed ? 'Expand status rail' : 'Collapse status rail'}
            aria-expanded={!collapsed}
            sx={{ color: 'text.secondary' }}
          >
            <ToggleIcon sx={{ fontSize: '20px' }} />
          </IconButton>
        </Tooltip>
      </Box>
      <Box sx={{ flex: 1, minHeight: 0, px: '8px', pb: '8px', ...scrollY }}>
        <List disablePadding>
          {TripDisplayStatusOrder.map((status) => {
            const count = counts[status] || 0;
            const countEl = (
              <Box
                component="span"
                sx={{
                  flexShrink: 0,
                  fontSize: '12px',
                  fontWeight: 600,
                  mt: '1px',
                  color: (theme) => (count > 0 ? theme.palette.text.secondary : theme.palette.text.inactive),
                }}
              >
                {count}
              </Box>
            );
            const item = (
              <ListItemButton
                key={status}
                selected={active === status}
                disableRipple
                aria-label={collapsed ? `${status}: ${count}` : undefined}
                onClick={() => {
                  setActive(status);
                  scrollToStatus((scopeRef && scopeRef.current) || document, status);
                }}
                sx={{
                  ...railItemSx,
                  display: 'flex',
                  // Collapsed: icon stacked over its count, centered in the column.
                  ...(collapsed
                    ? { flexDirection: 'column', alignItems: 'center', gap: '4px', px: 0 }
                    : { gap: '10px', alignItems: 'flex-start' }),
                  py: '10px',
                  // Empty statuses read as quietly grayed out, so the scrollspy
                  // highlight visibly skips over them as you scroll.
                  opacity: count > 0 ? 1 : 0.4,
                }}
              >
                <Box sx={{ display: 'flex', flexShrink: 0, mt: '1px' }}>
                  <TripDisplayStatusIcon status={status} />
                </Box>
                {!collapsed && (
                  <Typography sx={{ flex: 1, minWidth: 0, fontSize: '13px', fontWeight: 'inherit', lineHeight: 1.3 }}>
                    {status}
                  </Typography>
                )}
                {countEl}
              </ListItemButton>
            );
            // With the labels hidden, the status name moves into a tooltip.
            return collapsed ? (
              <Tooltip key={status} title={status} placement="right">
                {item}
              </Tooltip>
            ) : (
              item
            );
          })}
        </List>
      </Box>
    </Paper>
  );
};

export default TripsStatusRail;
